import React from 'react';
import './DashboardHome.css';
import HomeTopWidget from './components/HomeTopWidget';
import HomeActivity from './components/HomeActivity';
import HomeSubscribe from './components/HomeSubscribe';
import ClubsCTA from './components/ClubsCTA';
import ClubEvents from './components/ClubEvents';
import LatestVenuePerks from './components/LatestVenuePerks';
import { useDashboardData } from './hooks/useDashboardData';
import { useUser } from '../../../UserContext';
import { Link, useLocation } from 'react-router-dom';
import { appConfig } from '../../../config/appConfig';
import StateCard from '../../../platform/components/feedback/StateCard';
import { clearPendingOnboarding, getPendingOnboarding } from '../../../utils/onboardingState';
import { getMembershipCapabilities } from '../../../utils/membershipCapabilities';
import { 
    Loader, 
    Users, 
    MapPin, 
    Target, 
    Wrench,
    ArrowRight,
    Crown,
    X,
    PlusCircle,
    ClipboardCheck,
    UserPlus,
    Sparkles,
    AlertTriangle,
    CheckCircle2
} from 'lucide-react';

const DashboardHomePage = () => {
    const { data, loading, error } = useDashboardData();
    const { user } = useUser();
    const location = useLocation();
    const [showPromo, setShowPromo] = React.useState(true);
    const [onboardingState, setOnboardingState] = React.useState(null);
    const capabilities = getMembershipCapabilities(user?.subscription);

    React.useEffect(() => {
        const pendingOnboarding = getPendingOnboarding();
        if (pendingOnboarding) {
            setOnboardingState(pendingOnboarding);
        }
    }, [location.key]);

    const hasClubAccess = () => {
        return user?.clubAdminCount > 0 || user?.isAdmin;
    };

    const venueSingular = appConfig.terminology.venueSingular;
    const venuePlural = appConfig.terminology.venuePlural;
    const participantPlural = appConfig.terminology.participantPlural;
    const communitySingular = appConfig.terminology.communitySingular;

    const primaryActions = [
        {
            title: `Browse ${venuePlural}`,
            description: `Find clubs, ranges, and favourite ${venuePlural} to follow.`,
            icon: MapPin,
            linkTo: '/dashboard/clubs'
        },
        {
            title: 'Score Now',
            description: 'Jump straight into scoring and recent event management.',
            icon: Target,
            linkTo: '/dashboard/score'
        },
        {
            title: 'Create Event',
            description: 'Set up a new event and get your next score sheet ready.',
            icon: PlusCircle,
            linkTo: '/dashboard/score'
        },
        {
            title: 'Add Equipment',
            description: 'Start building your equipment history and cabinet records.',
            icon: Wrench,
            linkTo: '/dashboard/equipment'
        }
    ];

    const statItems = [
        {
            icon: Users,
            number: data.totalFriends,
            title: 'Connected Friends',
            color: 'blue',
            linkTo: '/dashboard/friends'
        },
        {
            icon: MapPin,
            number: data.totalClubs,
            title: 'Favourite Venues',
            color: 'green',
            linkTo: '/dashboard/clubs'
        },
        {
            icon: Target,
            number: data.totalShoots,
            title: 'Events Scored',
            color: 'orange',
            linkTo: '/dashboard/score'
        },
        {
            icon: Wrench,
            number: data.totalEquipment,
            title: 'Equipment Items',
            color: 'purple',
            linkTo: '/dashboard/equipment'
        }
    ];

    const checklistItems = [
        {
            key: 'venues',
            title: `Choose your first ${venueSingular}`,
            description: `Save a favourite ${venueSingular} so event discovery feels relevant.`,
            completed: data.totalClubs > 0,
            linkTo: '/dashboard/clubs'
        },
        {
            key: 'score',
            title: 'Log your first event',
            description: 'Create or join an event so the dashboard can start showing real progress.',
            completed: data.totalShoots > 0,
            linkTo: '/dashboard/score'
        },
        {
            key: 'equipment',
            title: 'Add your equipment',
            description: 'Track the gear you use and unlock better record keeping.',
            completed: data.totalEquipment > 0,
            linkTo: '/dashboard/equipment'
        },
        {
            key: 'friends',
            title: `Connect with ${participantPlural}`,
            description: 'Build your network so results and activity become more social.',
            completed: data.totalFriends > 0,
            linkTo: '/dashboard/friends'
        }
    ];

    const completedChecklistCount = checklistItems.filter((item) => item.completed).length;
    const showSetupChecklist = completedChecklistCount < checklistItems.length;
    const onboardingName = onboardingState?.firstName || user?.firstName || user?.userName;

    const dismissOnboardingWelcome = () => {
        clearPendingOnboarding();
        setOnboardingState(null);
    };

    if (loading) {
        return (
            <div className="dashboard-component dashboard-home">
                <StateCard
                    icon={Loader}
                    title="Loading your dashboard"
                    description="Pulling together your clubs, activity, events, and equipment."
                    loading
                />
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-component dashboard-home">
                <StateCard
                    icon={AlertTriangle}
                    title="We couldn't load your dashboard"
                    description="Please try again in a moment. If this keeps happening, the dashboard API responses likely need attention."
                    tone="error"
                />
            </div>
        );
    }

    return (
        <div className="dashboard-component dashboard-home">
            {/* Membership Status Card */}
            {!capabilities.isPaid && showPromo && (
                <div className="dashboard-membership-card">
                    <button className="promo-close" onClick={() => setShowPromo(false)}>
                        <X size={18} />
                    </button>
                    <div className="membership-card-content">
                        <div className="membership-card-icon">
                            <Crown size={32} />
                        </div>
                        <div className="membership-card-text">
                            <h3>Keep discovery open. Upgrade for power features.</h3>
                            <p>Free members can still browse venues, use marketplace, and follow news. {capabilities.upgradePrompt}</p>
                        </div>
                        <Link to="/dashboard/subscription" className="membership-card-cta">
                            See Plans
                            <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            )}

            {/* Club Admin Access Notice */}
            {hasClubAccess() && (
                <div className="dashboard-club-access">
                    <div className="club-access-content">
                        <MapPin size={20} />
                        <span>
                            {user?.isAdmin
                                ? 'You have platform admin access to manage all venues'
                                : <>You have admin access to <strong>{user.clubAdminCount} venue{user.clubAdminCount > 1 ? 's' : ''}</strong></>}
                        </span>
                    </div>
                    <Link to="/dashboard/manage-clubs" className="club-access-link">
                        Manage Venues
                        <ArrowRight size={16} />
                    </Link>
                </div>
            )}

            {capabilities.isVenue && (
                <div className="dashboard-club-access">
                    <div className="club-access-content">
                        <CheckCircle2 size={20} />
                        <span>
                            Your venue plan is designed separately from member Premium, so your club tools stay focused on operations, members, events, and offers.
                        </span>
                    </div>
                    <Link to="/dashboard/manage-clubs" className="club-access-link">
                        Open Venue Tools
                        <ArrowRight size={16} />
                    </Link>
                </div>
            )}

            {onboardingState && (
                <div className="dashboard-welcome-banner">
                    <button className="banner-close" onClick={dismissOnboardingWelcome}>
                        <X size={18} />
                    </button>
                    <div className="banner-content">
                        <div className="banner-icon">
                            <Sparkles size={24} />
                        </div>
                        <div className="banner-text">
                            <h3>Welcome{onboardingName ? `, ${onboardingName}` : ''}</h3>
                            <p>Your setup checklist is ready below. Start with a venue, your first event, or your equipment so the dashboard becomes personal straight away.</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="dashboard-actions-section">
                <div className="dashboard-section-heading">
                    <div>
                        <h3>Primary Actions</h3>
                        <p>Jump straight into the next thing most members need to do.</p>
                    </div>
                </div>
                <div className="dashboard-stats-grid dashboard-stats-grid-compact">
                    {statItems.map((item) => (
                        <HomeTopWidget
                            key={item.title}
                            icon={item.icon}
                            number={item.number}
                            title={item.title}
                            color={item.color}
                            linkTo={item.linkTo}
                        />
                    ))}
                </div>
                <div className="dashboard-actions-grid">
                    {primaryActions.map((action) => {
                        const Icon = action.icon;

                        return (
                            <Link key={action.title} to={action.linkTo} className="dashboard-action-card">
                                <div className="dashboard-action-icon">
                                    <Icon size={22} />
                                </div>
                                <div className="dashboard-action-content">
                                    <h4>{action.title}</h4>
                                    <p>{action.description}</p>
                                </div>
                                <ArrowRight size={18} className="dashboard-action-arrow" />
                            </Link>
                        );
                    })}
                </div>
            </div>

            {/* Stats Widgets - Sport Agnostic */}
            <div className="dashboard-stats-grid dashboard-stats-grid-desktop">
                {statItems.map((item) => (
                    <HomeTopWidget
                        key={item.title}
                        icon={item.icon}
                        number={item.number}
                        title={item.title}
                        color={item.color}
                        linkTo={item.linkTo}
                    />
                ))}
            </div>

            {/* Main Content Grid */}
            <div className="dashboard-main-grid">
                <div className="dashboard-main-left">
                    {showSetupChecklist && (
                        <div className="dashboard-setup-card">
                            <div className="dashboard-section-heading">
                                <div>
                                    <h3>Get Set Up</h3>
                                    <p>{completedChecklistCount} of {checklistItems.length} complete. Finish the basics to unlock a better dashboard.</p>
                                </div>
                                <div className="dashboard-setup-badge">
                                    <Sparkles size={16} />
                                    New member focus
                                </div>
                            </div>
                            <div className="dashboard-setup-list">
                                {checklistItems.map((item) => (
                                    <Link key={item.key} to={item.linkTo} className={`dashboard-setup-item ${item.completed ? 'is-complete' : ''}`}>
                                        <div className="dashboard-setup-item-icon">
                                            {item.completed ? <CheckCircle2 size={18} /> : <ClipboardCheck size={18} />}
                                        </div>
                                        <div className="dashboard-setup-item-content">
                                            <h4>{item.title}</h4>
                                            <p>{item.description}</p>
                                        </div>
                                        <ArrowRight size={18} />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                    <HomeActivity activities={data.recentActivity} currentUserId={user?.id} />
                </div>
                <div className="dashboard-main-right">
                    <HomeSubscribe />
                    <ClubsCTA />
                    <LatestVenuePerks />
                    <ClubEvents events={data.upcomingEvents} />
                    {data.totalFriends === 0 && (
                        <StateCard
                            icon={UserPlus}
                            title={`Build your ${participantPlural} network`}
                            description={`Add your first connected ${communitySingular} so shared scores and activity become more useful.`}
                            actionLabel="Find Friends"
                            actionTo="/dashboard/friends"
                            compact
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardHomePage;
